import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';
import { 
  menuItemsAPI, 
  categoriesAPI,
  getRestaurantId
} from '../utils/api';
import imageCompression from 'browser-image-compression';
import './MenuManagement.css';

function MenuManagement() {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image: '',
    images: [],
    diet: 'Veg',
    spice: 'Mild',
    popular: false,
    cuisine: '',
    enabled: true
  });

  // Helpers to persist custom categories per restaurant in localStorage
  const getStoredCategoriesKey = () => {
    const restaurantId = getRestaurantId() || 'default';
    return `MenuBazzar_categories_${restaurantId}`;
  };

  const loadStoredCategories = () => {
    try {
      const key = getStoredCategoriesKey();
      const stored = localStorage.getItem(key);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('Failed to load stored categories', e);
      return [];
    }
  };

  const saveStoredCategories = async (cats) => {
    try {
      // Save to localStorage
      const key = getStoredCategoriesKey();
      localStorage.setItem(key, JSON.stringify(cats));
      
      // Sync to backend so customer site sees updated categories
      try {
        await categoriesAPI.update(cats);
        console.log('Categories synced to backend');
      } catch (err) {
        console.warn('Failed to sync categories to backend:', err);
        // Don't throw - localStorage save succeeded
      }
    } catch (e) {
      console.warn('Failed to save categories', e);
    }
  };

  useEffect(() => {
    loadMenuItems();
  }, []);

  const loadMenuItems = async () => {
    try {
      const restaurantId = getRestaurantId();
      const [itemsResponse, categoriesResponse] = await Promise.all([
        menuItemsAPI.getAll(restaurantId),
        categoriesAPI.getAll()
      ]);

      const fetchedItems = itemsResponse.data.menuItems || [];
      const backendCategories = categoriesResponse.data.categories || [];

      // Load stored categories (localStorage is the source of truth)
      const storedCategories = loadStoredCategories();

      // If no stored categories exist yet, initialize from backend + used categories
      if (storedCategories.length === 0) {
        // Categories actually used in items (ensures previously used custom categories are never lost)
        const usedCategories = Array.from(
          new Set(
            fetchedItems
              .map(item => item.category)
              .filter(Boolean)
          )
        );

        const initialCategories = Array.from(
          new Set([
            ...backendCategories,
            ...usedCategories
          ])
        );

        setCategories(initialCategories);
        // Sync to backend immediately so customer site sees them
        await saveStoredCategories(initialCategories);
      } else {
        // Use stored categories (user's customized list)
        // This ensures deleted categories stay deleted after refresh
        setCategories(storedCategories);
        
        // Also sync to backend in case it wasn't synced before
        // (This ensures customer site always sees the latest categories)
        await saveStoredCategories(storedCategories);
      }

      setMenuItems(fetchedItems);
    } catch (error) {
      console.error('Error loading menu items:', error);
      setNotification({
        type: 'error',
        message: 'Failed to load menu items. Please try again.',
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImagesChange = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    if (!files.length) return;

    try {
      setNotification({
        type: 'info',
        message: 'Compressing images, please wait...',
      });

      // Compression options
      const options = {
        maxSizeMB: 0.5,          // Maximum 500KB per image after compression
        maxWidthOrHeight: 1024,   // Max dimension 1024px
        useWebWorker: true,
        fileType: 'image/jpeg',   // Convert to JPEG for better compression
      };

      const compressedFiles = [];
      
      for (const file of files) {
        try {
          const compressedFile = await imageCompression(file, options);
          compressedFiles.push(compressedFile);
        } catch (err) {
          console.error('Compression error for', file.name, err);
          setNotification({
            type: 'error',
            message: `Failed to compress "${file.name}". Please try a different image.`,
          });
          return;
        }
      }

      // Convert compressed files to base64
      const results = [];
      for (let idx = 0; idx < compressedFiles.length; idx++) {
        const file = compressedFiles[idx];
        const reader = new FileReader();
        
        await new Promise((resolve, reject) => {
          reader.onloadend = () => {
            results[idx] = reader.result;
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      const images = results.filter(Boolean).slice(0, 6);
      setFormData(prev => ({
        ...prev,
        images,
        image: images[0] || prev.image
      }));

      setNotification({
        type: 'success',
        message: `${images.length} image(s) compressed and ready to upload!`,
      });
    } catch (error) {
      console.error('Image handling error:', error);
      setNotification({
        type: 'error',
        message: 'Failed to process images. Please try again.',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = String(formData.name || '').trim();
    const trimmedDescription = String(formData.description || '').trim(); // optional
    const parsedPrice = Number.parseFloat(formData.price);
    const resolvedCategory = formData.category || categories[0] || 'Main Course';

    if (!trimmedName || !Number.isFinite(parsedPrice) || parsedPrice <= 0 || !resolvedCategory) {
      setNotification({
        type: 'error',
        message: 'Please fill Name, Price and Category before saving.',
      });
      return;
    }

    try {
      const images = formData.images || [];
      const itemData = {
        ...formData,
        name: trimmedName,
        description: trimmedDescription, // optional
        price: parsedPrice,
        category: resolvedCategory,
        enabled: formData.enabled !== false,
        images,
        image: images[0] || formData.image || null
      };

      if (editingItem) {
        await menuItemsAPI.update(editingItem.id, itemData);
      } else {
        await menuItemsAPI.create(itemData);
      }

      // If this category isn't in our list yet, add it and sync
      if (!categories.includes(resolvedCategory)) {
        const updated = [...categories, resolvedCategory];
        setCategories(updated);
        await saveStoredCategories(updated);
      }

      resetForm();
      await loadMenuItems();
    } catch (error) {
      console.error('Error saving menu item:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to save menu item. Please try again.',
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      price: item.price ? item.price.toString() : '',
      category: item.category || categories[0] || '',
      image: (item.images && item.images[0]) || item.image || '',
      images: item.images || (item.image ? [item.image] : []),
      diet: item.diet || 'Veg',
      spice: item.spice || 'Mild',
      popular: item.popular || false,
      cuisine: item.cuisine || '',
      enabled: item.enabled !== false
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await menuItemsAPI.delete(id);
        await loadMenuItems();
      } catch (error) {
        console.error('Error deleting menu item:', error);
        setNotification({
          type: 'error',
          message: error.message || 'Failed to delete menu item. Please try again.',
        });
      }
    }
  };

  const handleToggleEnabled = async (id, currentEnabled) => {
    try {
      await menuItemsAPI.toggleEnabled(id, !currentEnabled);
      await loadMenuItems();
    } catch (error) {
      console.error('Error toggling menu item:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to update menu item. Please try again.',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: categories[0] || '',
      image: '',
      images: [],
      diet: 'Veg',
      spice: 'Mild',
      popular: false,
      cuisine: '',
      enabled: true
    });
    setEditingItem(null);
    setShowAddModal(false);
  };

  // Category management handlers
  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) {
      setNotification({
        type: 'error',
        message: 'Category name cannot be empty.',
      });
      return;
    }

    if (categories.includes(name)) {
      setNotification({
        type: 'error',
        message: 'This category already exists.',
      });
      return;
    }

    const updated = [...categories, name];
    setCategories(updated);
    saveStoredCategories(updated);
    setNewCategoryName('');
  };

  const startEditCategory = (index) => {
    setEditingCategoryIndex(index);
    setEditingCategoryName(categories[index]);
  };

  const cancelEditCategory = () => {
    setEditingCategoryIndex(null);
    setEditingCategoryName('');
  };

  const handleSaveCategoryEdit = () => {
    const name = editingCategoryName.trim();
    if (!name) {
      setNotification({
        type: 'error',
        message: 'Category name cannot be empty.',
      });
      return;
    }

    if (categories.some((cat, idx) => idx !== editingCategoryIndex && cat === name)) {
      setNotification({
        type: 'error',
        message: 'Another category with this name already exists.',
      });
      return;
    }

    const updated = categories.map((cat, idx) => (idx === editingCategoryIndex ? name : cat));
    setCategories(updated);
    saveStoredCategories(updated);
    cancelEditCategory();
  };

  const handleDeleteCategory = (index) => {
    const catName = categories[index];

    // Warn if any items are using this category
    const hasItems = menuItems.some(item => item.category === catName);
    if (hasItems) {
      const confirmDelete = window.confirm(
        `Some menu items are using the category "${catName}".\n` +
        'They will keep this category name, but it will be removed from the category list.\n\n' +
        'Do you still want to remove this category from the list?'
      );
      if (!confirmDelete) return;
    }

    const updated = categories.filter((_, idx) => idx !== index);
    setCategories(updated);
    saveStoredCategories(updated);

    // If the current form is using this category, reset to first available
    if (formData.category === catName) {
      setFormData(prev => ({
        ...prev,
        category: updated[0] || ''
      }));
    }
  };

  const groupedItems = menuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="menu-management">
      {notification && (
        <div className={`admin-notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button
            type="button"
            className="admin-notification-close"
            onClick={() => setNotification(null)}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      )}
      <header className="management-header">
        <Link to="/dashboard" className="back-btn">
          <FaArrowLeft /> Back
        </Link>
        <h1 className="management-title">Menu Management</h1>
        <div className="management-header-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => setShowCategoriesModal(true)}
          >
            <FaEdit /> Manage Categories
          </button>
          <button className="add-item-btn" onClick={() => setShowAddModal(true)}>
            <FaPlus /> Add Item
          </button>
        </div>
      </header>

      <div className="management-content">
        {Object.keys(groupedItems).length === 0 ? (
          <div className="no-items">
            <p>No menu items. Add your first item!</p>
          </div>
        ) : (
          Object.keys(groupedItems).map(category => (
            <div key={category} className="category-section">
              <h2 className="category-title">{category}</h2>
              <div className="items-grid">
                {groupedItems[category].map(item => (
                  <div key={item.id} className={`menu-item-card ${!item.enabled ? 'disabled' : ''}`}>
                    <div className="item-image-container">
                      <img src={item.image || 'https://via.placeholder.com/300'} alt={item.name} className="item-image" />
                      {!item.enabled && (
                        <div className="disabled-overlay">
                          <span>Disabled</span>
                        </div>
                      )}
                    </div>
                    <div className="item-info">
                      <div className="item-header">
                        <h3 className="item-name">{item.name}</h3>
                        <button
                          className="toggle-btn"
                          onClick={() => handleToggleEnabled(item.id, item.enabled)}
                          title={item.enabled ? 'Disable item' : 'Enable item'}
                        >
                          {item.enabled ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </div>
                      <div className="item-meta">
                        {item.diet && <span className="meta-badge diet">{item.diet}</span>}
                        {item.popular && <span className="meta-badge popular">Popular</span>}
                      </div>
                      <div className="item-footer">
                        <span className="item-price">₹{item.price ? item.price.toFixed(0) : '0'}</span>
                        <div className="item-actions">
                          <button 
                            className="edit-btn"
                            onClick={() => handleEdit(item)}
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDelete(item.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
              <button className="close-modal-btn" onClick={resetForm}>
                <FaTimes />
              </button>
            </div>
            <form className="item-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Price (₹) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    step="1"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Diet Type</label>
                  <select
                    name="diet"
                    value={formData.diet}
                    onChange={handleInputChange}
                  >
                    <option value="Veg">Veg</option>
                    <option value="Non-Veg">Non-Veg</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the dish (ingredients, portion, etc.)"
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Dish Images (up to 6 angles)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                />
                {formData.images && formData.images.length > 0 && (
                  <div className="multi-image-grid">
                    {formData.images.map((img, idx) => (
                      <img key={idx} src={img} alt={`Preview ${idx + 1}`} className="image-preview" />
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="popular"
                      checked={formData.popular}
                      onChange={handleInputChange}
                    />
                    Mark as Popular
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={formData.enabled}
                      onChange={handleInputChange}
                    />
                    Enabled (visible to customers)
                  </label>
                </div>
              </div>

              <button type="submit" className="submit-btn">
                <FaSave /> {editingItem ? 'Update' : 'Add'} Item
              </button>
            </form>
          </div>
        </div>
      )}

      {showCategoriesModal && (
        <div className="modal-overlay" onClick={() => setShowCategoriesModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Categories</h2>
              <button
                className="close-modal-btn"
                onClick={() => {
                  setShowCategoriesModal(false);
                  cancelEditCategory();
                  setNewCategoryName('');
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="categories-manager">
              <div className="categories-list">
                {categories.length === 0 && (
                  <p className="no-items">No categories yet. Add your first category!</p>
                )}
                {categories.map((cat, index) => (
                  <div key={cat} className="category-row">
                    {editingCategoryIndex === index ? (
                      <>
                        <input
                          type="text"
                          value={editingCategoryName}
                          onChange={(e) => setEditingCategoryName(e.target.value)}
                          className="category-input"
                          autoFocus
                        />
                        <button
                          type="button"
                          className="icon-btn save"
                          onClick={handleSaveCategoryEdit}
                          title="Save"
                        >
                          <FaSave />
                        </button>
                        <button
                          type="button"
                          className="icon-btn cancel"
                          onClick={cancelEditCategory}
                          title="Cancel"
                        >
                          <FaTimes />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="category-name">{cat}</span>
                        <div className="category-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => startEditCategory(index)}
                            title="Edit category"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            className="icon-btn delete"
                            onClick={() => handleDeleteCategory(index)}
                            title="Remove from list"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className="add-category-row">
                <input
                  type="text"
                  placeholder="New category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleAddCategory}
                >
                  <FaPlus /> Add
                </button>
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={async () => {
                    try {
                      await categoriesAPI.update(categories);
                      setNotification({
                        type: 'success',
                        message: 'All categories saved to database.',
                      });
                    } catch (err) {
                      console.error('Failed to save categories:', err);
                      setNotification({
                        type: 'error',
                        message: err.message || 'Failed to save categories. Please try again.',
                      });
                    }
                  }}
                  title="Save all categories to database"
                >
                  <FaSave /> Save All Categories
                </button>
              </div>

              <p className="categories-hint">
                These categories are used only for your restaurant. Customers will see
                dishes grouped by these categories on your public menu.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuManagement;


